/**
 * TabNav Component
 * Horizontal tab bar for switching between CFO dashboard views
 * ARIA tabs pattern with keyboard navigation (Left/Right arrows)
 */

import { useCallback, useRef } from 'react';
import type { ReactNode, KeyboardEvent } from 'react';
import './TabNav.css';

export type TabId = 'overview' | 'cash-pnl' | 'compliance' | 'alerts' | 'ai-insights' | 'scaling-up' | 'revenue-target' | 'settings';

interface Tab {
  id: TabId;
  label: string;
  badge?: number;
}

interface TabNavProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  alertCount?: number;
}

const TABS: Omit<Tab, 'badge'>[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'cash-pnl', label: 'Cash & P&L' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'ai-insights', label: 'AI Insights' },
  { id: 'scaling-up', label: 'Scaling Up' },
  { id: 'revenue-target', label: 'Revenue Target' },
  { id: 'settings', label: 'Settings' },
];

export function TabNav({ activeTab, onTabChange, alertCount }: TabNavProps): ReactNode {
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = TABS.findIndex((t) => t.id === activeTab);
      let nextIndex = currentIndex;

      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % TABS.length;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
      } else if (e.key === 'Home') {
        nextIndex = 0;
      } else if (e.key === 'End') {
        nextIndex = TABS.length - 1;
      } else {
        return;
      }

      e.preventDefault();
      onTabChange(TABS[nextIndex].id);

      const tabList = tabListRef.current;
      if (tabList) {
        const buttons = tabList.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        buttons[nextIndex]?.focus();
      }
    },
    [activeTab, onTabChange]
  );

  return (
    <nav className="tab-nav" aria-label="Dashboard sections">
      <div className="tab-nav__list" role="tablist" ref={tabListRef}>
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          const badge = tab.id === 'alerts' && alertCount ? alertCount : undefined;

          return (
            <button
              key={tab.id}
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              className={`tab-nav__tab ${isActive ? 'tab-nav__tab--active' : ''}`}
              onClick={() => onTabChange(tab.id)}
              onKeyDown={handleKeyDown}
              type="button"
            >
              {tab.label}
              {badge !== undefined && badge > 0 && (
                <span className="tab-nav__badge" aria-label={`${badge} active alerts`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
